import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { plainToClass, classToClass } from 'class-transformer';
import { map, flatMap, catchError } from 'rxjs/operators';
import { StoryChunkLoadModel, StoryLoadConfigModel } from './state/story.state.model';
import { ChapterService } from './chapter.service';
import { StoryTranslationModel } from './models/story-translation.model';
import { StoryType } from './models/story-type.enum';
import { flatten } from 'lodash';
import { StoryLoadTypeEnum } from './models/story-load-type.enum';
import { EngineService } from '../engine/engine.service';
import { makeAsync } from '../shared/make-async';
import { uuid } from '../shared/uuid';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../engine/engine.service";
import * as i3 from "./chapter.service";
// #TODO: [EM] Relocate all calls to Human engine to engineService.
// #TODO: [EM] Replace loading API to use ChapterModel objects (data) rather than humanChapter objects (engine state)
/**
 * These are broken up for progressive loading
 */
const globalTypes = ['reflections', 'lights']; // Not bound to objects
const staticTypes = ['material', 'geometry', 'transform']; // Needed for render
const dynamicTypes = ['tweens', 'morph']; // Needed to animate
export class StoryService {
    constructor(http, engineService, chapterService) {
        this.http = http;
        this.engineService = engineService;
        this.chapterService = chapterService;
    }
    // get the object ids to be shown in the specified human chapter
    getShownObjectsFromChapter(humanChapter) {
        const synchronziation = humanChapter.synchronization;
        const objectIds = [];
        Object.entries(synchronziation).forEach(([objectId, sync]) => {
            if (sync.shown) {
                objectIds.push(objectId);
            }
        });
        return objectIds;
    }
    // create global/static/dynamic asset load chunks for the target chapter
    getTargetChapterLoadChunks(storyId, humanChapter, config) {
        const globaltargetChunk = new StoryChunkLoadModel();
        const statictargetChunk = new StoryChunkLoadModel();
        const dynamictargetChunk = new StoryChunkLoadModel();
        globaltargetChunk.storyId = statictargetChunk.storyId = dynamictargetChunk.storyId = storyId;
        globaltargetChunk.chapterId = statictargetChunk.chapterId = dynamictargetChunk.chapterId = humanChapter.id;
        // global assets of target chapter
        // only load globalTargetChunks if we are doing a fresh load, not a module import
        // but what happens if theres an asset that needs to be loaded that wasn't loaded by the first chapter?
        // only create globalTargetChunk if the 
        // I don't want to exclude globalTargetChunk.  I only want to set it as inactive.
        // why is it currently set to active?  Because its the target chapter. (the glitchyness is most likely a bug)
        // should this function know about the mode of operation?  It already has a StoryLoadConfigModel param
        // would this work If i took out brain_v02 root?  
        // activating global assets causes the glitch back to chapter 1..
        // so when do I need to activate globalTargetChunk?
        // when we're loading from a fresh start
        // if it's off for subsequent loads, we might not activate assets that didn't exist 
        // maybe order matters? -> no
        // What is it about global target chunks??
        globaltargetChunk.objects = Human.scene.rootObjects.map((humanObject) => humanObject.objectId);
        globaltargetChunk.assetTypes = globalTypes;
        globaltargetChunk.activate = true;
        globaltargetChunk.activationConfig = config.targetChapterConfig.global;
        globaltargetChunk.readyState = {};
        globaltargetChunk.time = 0; // #TODO: [EM] - query the true global time
        // static assets of target chapter
        statictargetChunk.objects = this.getShownObjectsFromChapter(humanChapter);
        statictargetChunk.assetTypes = staticTypes;
        statictargetChunk.activate = true;
        statictargetChunk.activationConfig = config.targetChapterConfig.static;
        statictargetChunk.readyState = { model: true };
        statictargetChunk.time = 0;
        // dynamic assets of target chapter
        dynamictargetChunk.objects = this.getShownObjectsFromChapter(humanChapter);
        dynamictargetChunk.assetTypes = dynamicTypes;
        dynamictargetChunk.activate = true;
        dynamictargetChunk.activationConfig = config.targetChapterConfig.dynamic;
        dynamictargetChunk.readyState = { animation: true };
        dynamictargetChunk.time = 0;
        return [globaltargetChunk, statictargetChunk, dynamictargetChunk];
    }
    // chunk.activate needs to be set to false for either
    // 
    // create content load chunks from the chapter
    getChapterLoadChunks(storyId, humanChapter, config) {
        let chunks = [];
        if (humanChapter.id === config.targetChapterId) {
            chunks = this.getTargetChapterLoadChunks(storyId, humanChapter, config);
        }
        else {
            const chapterObjets = this.getShownObjectsFromChapter(humanChapter);
            const _chunk = new StoryChunkLoadModel();
            _chunk.activate = false;
            _chunk.chapterId = humanChapter.id;
            _chunk.objects = chapterObjets;
            _chunk.storyId = storyId;
            if (config.chapterAssetBundle) {
                // queue up static/dynamic assets for joint loading
                _chunk.assetTypes = [...staticTypes, ...dynamicTypes, ...globalTypes];
                _chunk.readyState = { model: true, animation: true };
                chunks.push(_chunk);
            }
            else {
                // separate chunks for static and dynamic assets
                chunks.splice(0, 0, _chunk, classToClass(_chunk));
                chunks[0].assetTypes = staticTypes;
                chunks[0].readyState = { model: true };
                chunks[1].assetTypes = dynamicTypes;
                chunks[1].readyState = { animation: true };
            }
        }
        return chunks;
    }
    // create content load chunk for the rest of the scene graph
    getSceneGraphLoadChunk(storyId, roots, config) {
        const chunk = new StoryChunkLoadModel();
        chunk.activate = false;
        chunk.chapterId = null;
        chunk.objects = roots.map((root) => root.objectId);
        chunk.storyId = storyId;
        chunk.assetTypes = [...staticTypes];
        chunk.readyState = { model: true, animation: false };
        return chunk;
    }
    // create load chunks for all at once loading
    getAllAtOnceLoadChunks(storyId, config) {
        const chunk = new StoryChunkLoadModel();
        chunk.activate = true;
        chunk.chapterId = Human.timeline.rootTimelines[storyId]._chapters[0].id;
        chunk.objects = Human.scene.rootObjects.map((root) => root.objectId);
        chunk.storyId = storyId;
        chunk.assetTypes = [...globalTypes, ...staticTypes, ...dynamicTypes];
        chunk.readyState = { model: true, animation: true };
        return [chunk];
    }
    // create load chunks for progressive loading
    getProgressiveLoadChunks(storyId, config) {
        let chunks = [];
        // create chunks for chapters according to story load configuration
        Human.timeline.rootTimelines[storyId]._chapters.forEach((humanChapter) => {
            const _chapterChunks = this.getChapterLoadChunks(storyId, humanChapter, config);
            if (humanChapter.id === config.targetChapterId) {
                // target chapter chunks go first
                chunks = [..._chapterChunks, ...chunks];
            }
            else {
                // add non-target chunks in order
                chunks = [...chunks, ..._chapterChunks];
            }
        });
        // resort chunks based on config
        if (config.sortBy === 'readyState') {
            chunks.sort(this.compareReadyState);
        }
        // ensure that this is the last chunk
        chunks.push(this.getSceneGraphLoadChunk(storyId, Human.scene.rootObjects, config));
        return chunks;
    }
    // create load chunks for on-demand loading
    getOnDemandLoadChunks(storyId, config) {
        // if (Human.timeline.rootTimelines[storyId]._chapters.length === 2) {
        //   return mockChunks;
        // }
        let chunks = [];
        // create chunks for chapters according to story load configuration
        Human.timeline.rootTimelines[storyId]._chapters.forEach((humanChapter) => {
            const _chapterChunks = this.getChapterLoadChunks(storyId, humanChapter, config);
            if (humanChapter.id === config.targetChapterId) {
                // target chapter chunks go first
                chunks = [..._chapterChunks, ...chunks];
            }
            else {
                // add non-target chunks in order
                chunks = [...chunks, ..._chapterChunks];
            }
        });
        // resort chunks based on config
        if (config.sortBy === 'readyState') {
            chunks.sort(this.compareReadyState);
        }
        return chunks;
    }
    compareReadyState(chunk1, chunk2) {
        // 0 - readyState false, 1 - at least model readyState, 2  - animation only ready state, 3 - ready state undefined
        const scoreA = chunk1.readyState ? (chunk1.readyState.model ? 1 : (chunk1.readyState.animation ? 2 : 0)) : 3;
        const scoreB = chunk2.readyState ? (chunk2.readyState.model ? 1 : (chunk2.readyState.animation ? 2 : 0)) : 3;
        if (scoreA < scoreB) {
            return -1;
        }
        else if (scoreA === scoreB) {
            // chapter tie breaker
            const chapter1 = Human.timeline.rootTimelines[chunk1.storyId]._chapters.findIndex((chapter) => chapter.id === chunk1.chapterId);
            const chapter2 = Human.timeline.rootTimelines[chunk2.storyId]._chapters.findIndex((chapter) => chapter.id === chunk2.chapterId);
            return (chapter1 < chapter2) ? -1 : ((chapter1 === chapter2) ? 0 : 1);
        }
        else {
            return 1;
        }
    }
    // get the index of the chapter in the story
    getChapterIndex(storyId, chapterId) {
        if (Human.timeline.rootTimelines[storyId]) {
            const root = Human.timeline.rootTimelines[storyId];
            return root._chapters.findIndex((chapter) => chapter.id === chapterId);
        }
        else {
            return -1;
        }
    }
    // entry point to get either a module or bookmark
    getStory(id, type, lang = 'en') {
        /**
         * type: string => 'module' or 'bookmark'
         * id: string => id of content
         */
        // get content
        const story$ = type === 'bookmark' ? this.getBookmark(id) : this.getContent(id);
        // default English
        if (lang === 'en') {
            return story$;
        }
        else {
            let story;
            // transform content into target language
            return story$
                .pipe(flatMap((_story) => {
                story = _story;
                return this.getStoryTranslation(id, type, lang);
            }))
                .pipe(map((translation) => this.translateStory(story, translation)), catchError(() => story$));
        }
    }
    // get a biodigital module -> normalize chapter data
    getContent(path) {
        return this.http.get(`/content/modules/${path}.json`)
            .pipe(map((result) => {
            return result;
        }));
    }
    // get a biodigital bookmark -> normalize chapter data
    getBookmark(id) {
        return this.http.get(`/search/bookmarks/data`, { params: { be: id } })
            .pipe(map((result) => {
            return result.bookmark;
        }));
    }
    // create configuration for story loading
    getLoadConfig(storyId, targetChapterIdx = 0) {
        const config = new StoryLoadConfigModel();
        config.chapterAssetBundle = true;
        config.targetChapterId = Human.timeline.rootTimelines[storyId]._chapters[targetChapterIdx].id;
        config.targetChapterConfig = {
            global: { clipping: true, graph: true, camera: true, properties: true, transparencyMode: true, postprocess: true },
            static: { labels: true },
            dynamic: { timeline: true, particles: true }
        };
        config.sortBy = 'readyState';
        return config;
    }
    // Create the Human module
    // #NOTE: [EM] This cannot safely be assumed to be synchronous. Wrap in an observable.
    // This is the entrypoint into the engine
    // when a new module arrives, it needs to be added to the existing story.
    buildStory(storyId, story, activate) {
        Human.modules.moduleData[storyId] = story; // Place the JSON that into Human modules for local reference
        const moduleData = Object.assign({}, story);
        moduleData.moduleId = storyId; // needed by engine
        return new Observable(observer => {
            Human.modules.create(storyId, moduleData, () => {
                if (activate) {
                    Human.modules.activate(storyId, 0, {}, () => { });
                }
                observer.next(true);
                observer.complete();
            });
        });
    }
    getStoryChapters(storyId, storyType) {
        const humanModuleData = Human.modules.moduleData[storyId];
        return Human.timeline.rootTimelines[storyId]._chapters.map((humanChapter, idx) => {
            return this.chapterService.fromHumanChapter(humanModuleData, storyType, humanChapter, idx);
        });
    }
    // create list of load chunks for loading story content into engine
    getStoryLoadChunks(storyId, config, loadType) {
        let chunks = [];
        switch (loadType) {
            case StoryLoadTypeEnum.AllAtOnce:
                chunks = this.getAllAtOnceLoadChunks(storyId, config);
                break;
            case StoryLoadTypeEnum.Progressive:
                chunks = this.getProgressiveLoadChunks(storyId, config);
                break;
            case StoryLoadTypeEnum.OnDemand:
                chunks = this.getOnDemandLoadChunks(storyId, config);
                break;
            default:
                chunks = this.getProgressiveLoadChunks(storyId, config);
                break;
        }
        // give each chunk a unique ID.
        return chunks.map(chunk => {
            return Object.assign({}, chunk, { chunkId: uuid.idWithPrefix('chunk') });
        });
    }
    // load story chunk into engine according to chunk configuration
    loadStoryChunk(chunk, ok) {
        const objects = chunk.objects.map((id) => Human.scene.objects[id]); // ids to objects
        if (!!chunk.storyId) {
            // story based loading
            Human.modules.load(chunk.storyId, objects, chunk.assetTypes, () => {
                ok();
            });
        }
        else {
            // object based loading
            this.engineService.loadGraphObject(chunk.objects[0]).subscribe(() => {
                ok();
            });
        }
    }
    // #TODO: Support multiple module ids
    unloadStory(id, purgeAssets = true) {
        const ids = id ? [id] : Object.keys(Human.modules.modules);
        const deletions$ = ids.map((_id) => this.hardDeleteStory(_id, purgeAssets));
        return new Observable((observer) => {
            if (deletions$.length) {
                forkJoin(deletions$).subscribe(() => {
                    observer.next(true);
                    observer.complete();
                });
            }
            else {
                observer.next(true);
                observer.complete();
            }
        });
    }
    // create a load chunk for loading a objects
    createLoadObjectChunk(id) {
        const chunk = new StoryChunkLoadModel();
        chunk.assetTypes = [...staticTypes, ...dynamicTypes];
        chunk.objects = [id];
        return chunk;
    }
    hardDeleteStory(id, purgeAssets = true) {
        const assetTypes = ['geometries', 'morphs', 'reflections', 'tweens', 'lights', 'materials', 'transforms'];
        const storyGraph = id in Human.modules.modules ? Human.modules.modules[id].sceneGraph : [];
        const storyRoots = storyGraph.map((object) => Human.scene.objects[object.objectId]).filter(obj => !!obj);
        const storyObjects = flatten(storyRoots.map((root) => root.getLeafObjects()));
        const deactivate = makeAsync(Human.modules.deactivate, id, {});
        const unload = makeAsync(Human.modules.unload, id, storyObjects, assetTypes);
        const destroy = makeAsync(Human.modules.destroy, id);
        return new Observable(observer => {
            function endObserver() {
                observer.next();
                observer.complete();
            }
            // #TODO: [EM] Engine bug, never calls deactivate callback if target module is not currently active.
            const shouldDeactivate = Human.timeline.activeRoot && Human.timeline.activeRoot.id === id;
            (shouldDeactivate ? deactivate() : Promise.resolve({}))
                .then(() => (purgeAssets ? unload() : Promise.resolve({})))
                .then(() => destroy())
                .then(() => endObserver());
        });
    }
    /**
     * Gets the translation data for the specified content.
     */
    getStoryTranslation(id, type, lang) {
        // translation data endpoint
        const url = type === 'bookmark' ? `/search/bookmarks/tr/${lang}/${id}` : `/tr/${lang}/${id}.json`;
        return this.http.get(url)
            .pipe(map((result) => {
            return plainToClass(StoryTranslationModel, result);
        }));
    }
    /**
     * Check the requested id to determine if requested content is a bookmark or story
     * @param id Bookmark or module id
     */
    getStoryType(id) {
        return (id.indexOf('/') >= 0) ? StoryType.Module : StoryType.Bookmark;
    }
    /**
     * Translate the specified story based on the input translations.
     */
    translateStory(story, translation) {
        const chapters = story.chapters || [];
        const sceneGraph = story.sceneGraph || [];
        const translatedObjects = translation.objects || {};
        const translatedChapters = translation.chapters || [];
        // Translate story title + description
        story.displayName = translation.displayName || story.displayName;
        story.description = translation.description || story.description;
        // Translate scene graph
        sceneGraph.forEach((graph) => {
            const objects = graph.objects || [];
            objects.forEach((object) => {
                this.translateObject(object, translatedObjects);
            });
        });
        // Translate chapters
        chapters.forEach((chapter, chapterIndex) => {
            const translatedChapter = translatedChapters[chapterIndex];
            if (translatedChapter) {
                this.translateMetaInfo(chapter, translatedChapter);
                // Translate annotations
                const annotations = chapter.annotations || [];
                const translatedAnnotations = translatedChapter.annotations || [];
                annotations.forEach((annotation, annotationIndex) => {
                    const translatedAnnotation = translatedAnnotations[annotationIndex];
                    if (translatedAnnotation) {
                        this.translateMetaInfo(annotation, translatedAnnotation);
                    }
                });
                // Translate hotspots
                const hotspots = chapter.hotspots || [];
                const translatedHotspots = translatedChapter.hotspots || [];
                hotspots.forEach((hotspot, hotspotIndex) => {
                    const translatedHotspot = translatedHotspots[hotspotIndex];
                    if (translatedHotspot) {
                        this.translateMetaInfo(hotspot, translatedHotspot);
                    }
                });
            }
        });
        // #TODO[AC]: translate global hotspots
        return story;
    }
    translateMetaInfo(state, metaInfo) {
        const { title, description } = metaInfo;
        // translate title
        if (state.hasOwnProperty('displayName')) {
            state.displayName = title;
        }
        if (state.hasOwnProperty('title')) {
            state.title = title;
        }
        // translate description
        if (state.hasOwnProperty('description')) {
            state.description = description;
        }
    }
    translateObject(object, translatedObjects) {
        const objectId = object.objectId;
        const translatedDisplayName = translatedObjects[objectId];
        // Translate object
        if (translatedDisplayName) {
            object.displayName = translatedDisplayName;
        }
        // Translate children
        const childObjects = object.objects;
        if (childObjects) {
            childObjects.forEach((childObject) => {
                this.translateObject(childObject, translatedObjects);
            });
        }
    }
    // copy normalized labels to their respective chapters
    storeLabelsInChapters(labels, chapters) {
        chapters.forEach((chapter) => {
            chapter.labels = labels.filter(label => label.chapterId === chapter.id);
        });
    }
}
StoryService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function StoryService_Factory() { return new StoryService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.EngineService), i0.ɵɵinject(i3.ChapterService)); }, token: StoryService, providedIn: "root" });
